import { Component, OnInit, OnDestroy, Input, ElementRef } from "@angular/core";
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { RequestOptions } from "@angular/http";
import { Angular2TokenService } from "angular2-token";
import { Router, ActivatedRoute } from "@angular/router";
import { Store } from "@ngrx/store";
import { Actions } from "@ngrx/effects";
import { AppState } from "@store/app-state";
import { Subscription } from "rxjs/Subscription";
import {
  CompleterData,
  RemoteData,
  CompleterService,
  CompleterItem
} from "ng2-completer";
import { CommsStore } from "@store/comms";
import { ConstantsStore } from "@store/constants";
import { Term } from "@models/term";
import { Template } from "@models/template";
import { templatesUrl, commsUrl } from "@services/comm";
import { Recipient } from "@models/recipient";
import { SendCommParams, EmailPreviewParams, NumberOfRecipientsParams } from "@services/comm";
import {
  FETCH_EMAIL_PREVIEW,
  GET_NUMBER_OF_RECIPIENTS,
  SEND_COMM,
  SENT_COMM
} from "@reducers/comms";
import { SMS_TEMPLATES } from "@constants/sms-templates";

const SEND_TO_EMAIL_TYPES = [
  { title: "Student", value: 0 },
  { title: "Parent", value: 1 },
  { title: "Staff", value: 2 },
]

const SEND_TO_SMS_TYPES = [
  { title: "Student", value: 0 },
  { title: "Parent", value: 1 },
  { title: "Staff", value: 2 },
  { title: "Group", value: 3 },
]

@Component({
  selector: 'comms-form',
  templateUrl: './comms-form.pug',
})
export class CommsFormComponent implements OnInit, OnDestroy {

  @Input() public mode: string = "email"

  private subscriptions: Subscription[] = []

  public terms: Term[] = []
  public templates: Template[] = []

  public activeTerm: Term
  public activeTemplate: Template
  public recipients: Recipient[] = []
  public activeSendTo: number = null
  public activeVariablePreviewRecipient: Recipient = null

  public isInVariablePreviewMode: boolean = false
  public isUsingCustomisedTemplate: boolean = false
  public isSendingComm: boolean = false
  public isSuccessFlashVisible: boolean = false
  public numberOfRecipients: number = 0

  public sanatizedTemplateBodyHtml: SafeHtml = null
  public sanatizedVariablePreviewHtml: SafeHtml = null
  public editedTemplateBody: string = null

  public termSearchStr: string
  public termCompleter: CompleterData

  public templateSearchStr: string
  public templateCompleter: CompleterData

  public recipientSearchStr: string
  public recipientCompleter: RemoteData

  public sendToSearchStr: string
  public sendToSearchCompleter: CompleterData

  public sendToSmsSearchStr: string
  public sendToSmsCompleter: CompleterData

  public variablePreviewSearchStr: string
  public variablePreviewCompleter: CompleterData

  public enquiryId: number = null
  public enquirySendTo: string = null
  public enquiryName: string = null
  public isSendingSpecialEnquiryEmail: boolean = false

  constructor(
    private store: Store<AppState>,
    private actions: Actions,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private completerService: CompleterService,
    private http: Angular2TokenService,
    private htmlSanitizer: DomSanitizer,
    private el: ElementRef
  ) {}

  ngOnInit() {
    this.subscriptions.push(
      this.store.select("commsReducer")
        .subscribe((s: CommsStore) => {
          this.templates = s.templates
          this.sanatizedVariablePreviewHtml = this.sanatiseHtml(s.emailPreview)
          this.numberOfRecipients = s.numberOfRecipients
        }))

    this.subscriptions.push(
      this.store.select("constantsReducer")
        .subscribe((s: ConstantsStore) => {
          this.terms = s.constants.terms

          if (!this.activeTerm) { this.activeTerm = this.terms[0] }
        }))

    switch (this.mode) {
      case "email": {
        this.setupEmailCompleters()
        this.handleSpecialEnquiryEmailScenario()
        this.setupVariablePreviewCompleter()
        break
      }
      case "sms": {
        this.setupSmsCompleters()
        this.setupInitialSmsState()
        break
      }
    }

    this.setupSharedCompleters()
  }

  setupEmailCompleters() {

    this.sendToSearchCompleter =
      this.completerService.local(SEND_TO_EMAIL_TYPES, "title", "title")

    this.templateCompleter =
      this.completerService.local(this.templates, "description", "description")

  }

  setupSharedCompleters() {
    this.termCompleter =
      this.completerService.local(this.terms, "name", "name")

    this.setupRecipientCompleter()
  }

  setupSmsCompleters() {
    this.templateCompleter    =
      this.completerService.local(SMS_TEMPLATES, "description", "description")

    this.sendToSearchCompleter =
      this.completerService.local(SEND_TO_SMS_TYPES, "title", "title")
  }

  setupRecipientCompleter() {
    if (this.activeTerm) {
      const url = `${commsUrl}/recipients?term_id=${this.activeTerm.id}&query=`
      this.recipientCompleter   =
        this.completerService.remote(url, "title,query", "title")

      this.recipientCompleter.requestOptions(
        new RequestOptions({
          headers: this.http.currentAuthHeaders,
        })
      )
    }
  }

  setupVariablePreviewCompleter() {
    this.variablePreviewCompleter =
      this.completerService.local(this.recipients, "title", "title")
  }

  setupInitialSmsState() {
    // NOTE: For SMS this is always true. The template is merely a scaffold for
    // the customer rep to customise and send.
    this.isUsingCustomisedTemplate = true
  }

  isSendToPickerVisible(): boolean {
    let result = false

    switch (this.mode) {
      case "email": {
        result = !!this.activeTemplate &&
          this.activeTemplate.labels.includes("allUsers")
        break
      }
      case "sms": {
        result = true
      }
    }

    return result
  }

  isToggleVariablePreviewEnabled(): boolean {
    return this.activeTemplate && this.activeTerm && this.recipients.length > 0
  }

  clickedToggleVariablePreview() {
    if (this.isInVariablePreviewMode) {
      this.isInVariablePreviewMode = false
      this.sanatizedVariablePreviewHtml = null
    }
    else {
      this.isInVariablePreviewMode = true
    }
  }

  clickedRemoveRecipient(recipient: Recipient) {
    this.recipients = this.recipients.filter(r => r != recipient)
    this.setupVariablePreviewCompleter()
    this.refreshNumberOfRecipients()
  }

  clickedCustomiseTemplate() {
    this.isUsingCustomisedTemplate = !this.isUsingCustomisedTemplate
    this.sanatizedVariablePreviewHtml = null
  }

  clickedSend() {
    const payload: SendCommParams = {
      recipients: this.recipients,
      sendTo: this.activeSendTo,
      termId: this.activeTerm.id,
      templateId: this.activeTemplate.id,
      enquirySendTo: this.enquirySendTo,
      enquiryId: this.enquiryId,
    }

    this.checkAndSetEditableTemplateBodyForEmailMode()
    if (this.editedTemplateBody) {
      payload.edited_template_body = this.editedTemplateBody
    }

    this.isSendingComm = true

    this.actions
      .ofType(SENT_COMM)
      .take(1)
      .subscribe(() => {
        this.isSendingComm = false
        this.flashSuccessMessage()
      })

    this.store.dispatch({ type: SEND_COMM, payload })
  }

  flashSuccessMessage() {
    this.isSuccessFlashVisible = true
    this.recipients = []
    setTimeout(() => {
      this.isSuccessFlashVisible = false
    }, 5000)
  }

  selectedTerm(e: CompleterItem) {
    if (e)  { this.activeTerm = e.originalObject as Term }
    else    { this.activeTerm = null }

    this.setupRecipientCompleter()
  }

  selectedTemplate(e: CompleterItem) {
    if (e) {
      this.activeTemplate = e.originalObject as Template

      switch (this.mode) {
        case "email": {
          this.sanatizedTemplateBodyHtml = this.sanatiseHtml(this.activeTemplate.body)
          break
        }
        case "sms": {
          this.editedTemplateBody = this.activeTemplate.body
          break
        }
      }
    }
    else    { this.activeTemplate = null }
  }

  sanatiseHtml(htmlString: string): SafeHtml {
    if (htmlString) {
      return this.htmlSanitizer.bypassSecurityTrustHtml(htmlString)
    }
    else {
      return null
    }

  }

  selectedRecipient(e: CompleterItem) {
    if (e) {
      const recipient = Recipient.clone(e.originalObject)
      this.recipients.push(recipient)
      this.setupVariablePreviewCompleter()
      this.refreshNumberOfRecipients()
    }
  }

  selectedSendTo(e: CompleterItem) {
    if (e) {
      this.activeSendTo = e.originalObject.value
    }
    else {
      this.activeSendTo = null
    }
  }

  selectedVariablePreview(e: CompleterItem) {
    if (e) {
      this.activeVariablePreviewRecipient = e.originalObject as Recipient

      if (this.activeTerm && this.activeTemplate && this.activeVariablePreviewRecipient) {
        const payload: EmailPreviewParams = {
          term_id: this.activeTerm.id,
          template_id: this.activeTemplate.id,
        }

        if (this.activeVariablePreviewRecipient.matrixClassId) {
          payload.matrix_class_id = this.activeVariablePreviewRecipient.matrixClassId
        }

        if (this.activeVariablePreviewRecipient.courseId) {
          payload.course_id = this.activeVariablePreviewRecipient.courseId
        }

        if (this.activeVariablePreviewRecipient.contactId) {
          payload.contact_id = this.activeVariablePreviewRecipient.contactId
        }

        if (this.activeVariablePreviewRecipient.grade) {
          payload.grade = this.activeVariablePreviewRecipient.grade
        }

        this.checkAndSetEditableTemplateBodyForEmailMode()

        if (this.editedTemplateBody) {
          payload.edited_template_body = this.editedTemplateBody
        }

        this.store.dispatch({ type: FETCH_EMAIL_PREVIEW, payload })
      }

    }
    else {
      this.activeVariablePreviewRecipient = null
    }
  }

  isOriginalPreviewVisible(): boolean {
    return !this.isUsingCustomisedTemplate && this.mode == "email"
  }

  isEditablePreviewVisible(): boolean {
    return this.isUsingCustomisedTemplate && this.mode == "email"
  }

  isVariablePreviewVisible(): boolean {
    return this.isInVariablePreviewMode && !!this.sanatizedVariablePreviewHtml && this.mode == "email"
  }
  
  isCustomiseButtonVisible(): boolean {
    return !this.isInVariablePreviewMode && this.mode == "email"
  }

  isSmsEditorVisible(): boolean {
    return this.mode == "sms"
  }

  isSendButtonVisible(): boolean {
    return !this.isInVariablePreviewMode
  }

  isPreviewAreaVisible(): boolean {
    return !!this.sanatizedTemplateBodyHtml || this.mode == "sms"
  }

  isToggleVariablePreviewBtnVisible(): boolean {
    return this.mode == "email"
  }

  isFormvalid(): boolean {
    let result = true

    switch (this.mode) {
      case "email": {
        if (!this.activeTerm)             { result = false }
        if (!this.activeTemplate)         { result = false }
        if (this.recipients.length == 0)  { result = false }
        if (this.isSendToPickerVisible() && this.activeSendTo === null) {
          result = false }
        break
      }
      case "sms": {
        if (!this.activeTerm)             { result = false }
        if (!this.editedTemplateBody)     { result = false }
        if (this.recipients.length == 0)  { result = false }
        if (this.activeSendTo == null)    { result = false }
        break
      }
    }


    return result
  }

  checkAndSetEditableTemplateBodyForEmailMode() {
    if (this.mode != "email") { return }

    if (this.isUsingCustomisedTemplate) {
      this.editedTemplateBody = 
        this.el.nativeElement.querySelectorAll(".cf__editable-preview")[0].innerHTML
    }
    else {
      this.editedTemplateBody = null
    }
  }

  refreshNumberOfRecipients() {
    if (this.activeTemplate && this.recipients.length > 0) { 
      const payload: NumberOfRecipientsParams = {
        recipients: this.recipients,
        template_id: this.activeTemplate.id
      }

      this.store.dispatch({ type: GET_NUMBER_OF_RECIPIENTS, payload })
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe())
  }

  // TODO: This is a hacky thing put in becuase old SIS behaves this way.
  // Perhaps there's a better UI approach (why is this email so besopke?
  // are there other emails that need to act like this?)...
  // Note, the form elements are merely hidden with display: none via a
  // class on the root element.

  handleSpecialEnquiryEmailScenario() {
    const qp = this.activatedRoute.snapshot.queryParams

    if (!qp.enquiryId || !qp.sendTo || !qp.name) { return }

    this.enquiryId        = qp.enquiryId
    this.enquirySendTo    = qp.sendTo
    this.enquiryName      = qp.name

    this.activeTemplate   = this.templates.find((t) => {
      return t.slug == "online-enrolment-email-template" })

    this.activeTerm       = this.terms[0]
    this.recipients       = [new Recipient()]

    this.sanatizedTemplateBodyHtml =
      this.sanatiseHtml(this.activeTemplate.body)

    this.isSendingSpecialEnquiryEmail = true
  }

}



// WEBPACK FOOTER //
// /Users/jason/src/matrix/client/src/app/components/comms-form.ts